import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Skeleton,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper
} from "@mui/material";
import { Schedule } from "@mui/icons-material";
import CupidCardsSection from "./sections/CupidCardsSection";
import { shouldRenderCard } from "../../utils/cardStatus";
import { parseKSTDate } from "../../utils/dateUtils";
import HeroBanner from "./sections/HeroBanner";
import QuickActions from "./sections/QuickActions";
import RightRail from "./sections/RightRail";
import DashboardHeader from "./sections/DashboardHeader";
import DesktopSidebar from "./sections/DesktopSidebar";
import DesktopTopbar from "./sections/DesktopTopbar";

import type { DashboardSummary, CupidCard } from "./types/dashboard";
import { getDashboard } from "./api/dashboardApi";
import { openCard, extendCard } from "./api/cardsApi";
import { getUserProfile } from "./api/profileApi";
import { ReviewStatus } from "./types/profile";
import MyProfileModal from "./components/MyProfileModal";
import RejectedStatusPage from "./components/RejectedStatusPage";
import ApprovedStatusPage from "./components/ApprovedStatusPage";
import { useAuth } from "../../contexts/AuthContext";
import { useHeart } from "../../contexts/HeartContext";
import { getUnreadCount } from "../Messages/api/messagesApi";
import { getUnreadCount as getUnreadNotificationCount } from "../Notifications/api/notificationsApi";
import CardExtendDialog from "../../components/CardExtendDialog";
import { calculateCardExtendCost } from "../../utils/heart";
import type { CardExtendCost } from "../../types/heart";

// PENDING 상태 컴포넌트 분리
function PendingStatusPage({ onLogout }: { onLogout: () => void }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Schedule sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            심사 중입니다
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            회원님의 프로필이 현재 관리자 검토 중입니다.<br />
            심사가 완료되면 알림으로 결과를 알려드립니다.
          </Typography>
          <Typography variant="caption" display="block" color="text.disabled" sx={{ mb: 3 }}>
            승인 상태: <strong>대기중</strong>
          </Typography>
          <Button variant="contained" onClick={onLogout} sx={{ mt: 2 }}>
            로그아웃
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}


export default function DashboardPage() {
  const { user, logout, updateUser } = useAuth();
  const { balance: heartBalance, refreshBalance: refreshHeartBalance } = useHeart();
  const navigate = useNavigate();
  const [data, setData] = React.useState<DashboardSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [purchaseDialog, setPurchaseDialog] = React.useState(false);
  const [profileModal, setProfileModal] = React.useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = React.useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = React.useState(0);
  const [showExtendDialog, setShowExtendDialog] = React.useState(false);
  const [extendDialogData, setExtendDialogData] = React.useState<{
    cardId: string;
    cost: CardExtendCost;
    isMutual: boolean;
  } | null>(null);
  const [showApprovalCelebration, setShowApprovalCelebration] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getDashboard();
      setData(res);
    } catch (e: any) {
      setError(e?.message ?? "대시보드를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = React.useCallback(async (cardId: string) => {
    if (!data) return;
    try {
      const result = await openCard(cardId);
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          cupidCards: prev.cupidCards.map(card =>
            card.cardId === cardId
              ? {
                  ...card,
                  status: "OPENED" as const,
                  thumbnailImage: result.thumbnailImage,
                  cardPreview: {
                    ...card.cardPreview,
                    name: result.targetProfile.name,
                    age: result.targetProfile.age,
                    residenceRegion: result.targetProfile.residenceRegion,
                    interests: result.targetProfile.interests,
                    hasChat: false,
                    chatId: null
                  }
                }
              : card
          ),
          wallet: {
            ...prev.wallet,
            hearts: prev.wallet.hearts - (prev.cupidCards.find(c => c.cardId === cardId)?.openCostHearts ?? 1)
          }
        };
      });
      // HeartContext 업데이트
      await refreshHeartBalance();
    } catch (error: any) {
      setError(error?.message ?? "카드를 열 수 없습니다.");
    }
  }, [data, refreshHeartBalance]);

  const handleExtend = React.useCallback((cardId: string) => {
    if (!data) return;

    const targetCard = data.cupidCards.find(c => c.cardId === cardId);
    if (!targetCard) return;

    const expiresAt = parseKSTDate(targetCard.expiresAt);
    const currentExtensions = targetCard.extendCount;

    const cost = calculateCardExtendCost(
      heartBalance,
      expiresAt,
      currentExtensions,
      parseKSTDate(targetCard.extendDeadlineAt),
      targetCard.extendDeadlineAt
    );

    setExtendDialogData({ cardId, cost, isMutual: targetCard.isMutual ?? false });
    setShowExtendDialog(true);
  }, [data, heartBalance]);

  const handleExtendConfirm = React.useCallback(async () => {
    if (!extendDialogData) return;

    try {
      const result = await extendCard(extendDialogData.cardId);
      setData(prev => {
        if (!prev) return prev;
        const targetCard = prev.cupidCards.find(c => c.cardId === extendDialogData.cardId);
        if (!targetCard) return prev;

        return {
          ...prev,
          cupidCards: prev.cupidCards.map(card =>
            card.cardId === extendDialogData.cardId
              ? {
                  ...card,
                  expiresAt: result.expiresAt,
                  extendDeadlineAt: result.extendDeadlineAt,
                  extendCount: result.extendCount,
                  remainingDays: result.remainingDays,
                  isExtendable: result.canExtend,
                  extendCostHearts: result.extendCostHearts
                }
              : card
          ),
          wallet: {
            ...prev.wallet,
            hearts: prev.wallet.hearts - (targetCard.extendCostHearts ?? 3)
          }
        };
      });

      // HeartContext 업데이트
      await refreshHeartBalance();

      setShowExtendDialog(false);
      setExtendDialogData(null);
    } catch (error: any) {
      setError(error?.message ?? "카드를 연장할 수 없습니다.");
      setShowExtendDialog(false);
      setExtendDialogData(null);
    }
  }, [extendDialogData, refreshHeartBalance]);

  const handleExtendCancel = React.useCallback(() => {
    setShowExtendDialog(false);
    setExtendDialogData(null);
  }, []);

  const handleChat = React.useCallback((card: CupidCard) => {
    navigate(`/messages?cardId=${card.cardId}`);
  }, [navigate]);

  const handleHeartsPurchase = React.useCallback(() => {
    navigate('/heart/purchase');
  }, [navigate]);

  const handleWallet = React.useCallback(() => {
    navigate('/wallet');
  }, [navigate]);

  const handleProfile = React.useCallback(() => {
    setProfileModal(true);
  }, []);

  const handleMessages = React.useCallback(() => {
    navigate('/messages');
  }, [navigate]);

  const handleAdmin = React.useCallback(() => {
    window.location.href = '/admin';
  }, []);

  const handleLogout = React.useCallback(async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('로그아웃 실패:', error);
      navigate('/');
    }
  }, [logout, navigate]);

  const handleApprovalNavigate = React.useCallback(() => {
    setShowApprovalCelebration(false);
  }, []);

  // 사용자 상태 검사
  React.useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    if (user.status === 'PENDING' || user.status === 'REJECTED') {
      setLoading(false);
      // localStorage 캐시가 stale할 수 있으므로 서버에서 최신 userStatus 확인
      if (user.status === 'PENDING') {
        getUserProfile()
          .then(profile => {
            if (profile.userStatus === 'APPROVED' || profile.reviewStatus === ReviewStatus.APPROVED) {
              updateUser({ status: 'APPROVED' as any });
              setShowApprovalCelebration(true);
            } else if (profile.userStatus === 'REJECTED' || profile.reviewStatus === ReviewStatus.REJECTED) {
              updateUser({ status: 'REJECTED' as any });
            }
          })
          .catch(() => {});
      }
      return;
    }
    
    if (user.status === 'APPROVED') {
      load();
      refreshHeartBalance();
      // 읽지 않은 메시지 수 조회
      getUnreadCount().then(setUnreadMessageCount).catch(() => {});
      // 읽지 않은 알림 수 조회
      getUnreadNotificationCount().then(r => setUnreadNotificationCount(r.unreadCount)).catch(() => {});
    } else {
      navigate('/login');
    }
  }, [user, navigate, load, refreshHeartBalance]);

  // Short polling: PENDING 상태일 때 30초마다 심사 결과 갱신
  const REVIEW_POLL_INTERVAL = 30000;
  React.useEffect(() => {
    if (user?.status !== 'PENDING') return;

    const poll = setInterval(async () => {
      if (document.hidden) return;
      try {
        const profile = await getUserProfile();
        if (profile.userStatus === 'APPROVED' || profile.reviewStatus === ReviewStatus.APPROVED) {
          updateUser({ status: 'APPROVED' as any });
          setShowApprovalCelebration(true);
        } else if (profile.userStatus === 'REJECTED' || profile.reviewStatus === ReviewStatus.REJECTED) {
          updateUser({ status: 'REJECTED' as any });
        }
      } catch {
        // 에러 시 다음 interval에 재시도
      }
    }, REVIEW_POLL_INTERVAL);

    return () => clearInterval(poll);
  }, [user?.status, updateUser]);

  // Short polling: 30초마다 읽지 않은 메시지 수 갱신
  const BADGE_POLL_INTERVAL = 30000;
  React.useEffect(() => {
    if (user?.status !== 'APPROVED') return;

    const poll = setInterval(async () => {
      if (document.hidden) return;
      try {
        const count = await getUnreadCount();
        setUnreadMessageCount(count);
      } catch {
        // 에러 시 다음 interval에 재시도
      }
    }, BADGE_POLL_INTERVAL);

    return () => clearInterval(poll);
  }, [user?.status]);

  // Short polling: 30초마다 읽지 않은 알림 수 갱신 (헤더 뱃지)
  const NOTIFICATION_POLL_INTERVAL = 30000;
  React.useEffect(() => {
    if (user?.status !== 'APPROVED') return;

    const poll = setInterval(async () => {
      if (document.hidden) return;
      try {
        const { unreadCount } = await getUnreadNotificationCount();
        setUnreadNotificationCount(unreadCount);
      } catch {
        // 에러 시 다음 interval에 재시도
      }
    }, NOTIFICATION_POLL_INTERVAL);

    return () => clearInterval(poll);
  }, [user?.status]);

  // 승인 축하 화면
  if (showApprovalCelebration) {
    return <ApprovedStatusPage onNavigateDashboard={handleApprovalNavigate} />;
  }

  // PENDING/REJECTED 상태일 때 별도 화면 렌더링
  if (user?.status === 'PENDING') {
    return <PendingStatusPage onLogout={handleLogout} />;
  }

  if (user?.status === 'REJECTED') {
    return <RejectedStatusPage onLogout={handleLogout} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* 헤더 - 모바일만 표시 */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <DashboardHeader
          hearts={heartBalance}
          unreadNotifications={unreadNotificationCount}
          userRole={user?.role}
          profileImageUrl={user?.profileImageUrl}
          displayName={data?.user.userName ?? "사용자"}
          onHeartsPurchase={handleHeartsPurchase}
          onWallet={handleWallet}
          onProfile={handleProfile}
          onAdmin={user?.role === 'ADMIN' ? handleAdmin : undefined}
          onSettings={() => navigate('/settings')}
          onSupport={() => navigate('/support')}
          onNotifications={() => navigate('/notifications')}
          onLogout={handleLogout}
          loading={loading}
        />
      </Box>

      <Box sx={{ display: 'flex', flex: 1 }}>
        {/* 데스크탑 사이드바 */}
        <DesktopSidebar
          displayName={data?.user.userName ?? "사용자"}
          userRole={user?.role}
          unreadCount={unreadMessageCount}
          onProfile={handleProfile}
          hearts={heartBalance}
          onHeartsPurchase={handleHeartsPurchase}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* 데스크탑 상단바 */}
        <DesktopTopbar
          displayName={data?.user.userName ?? "사용자"}
          onProfile={handleProfile}
          onLogout={handleLogout}
        />
        <Container
          maxWidth="xl"
          sx={{
            py: { xs: 0, sm: 2, md: 3 },
            px: { xs: 0, sm: 2, md: 3 }
          }}
        >
        {loading && (
          <Box>
            <Skeleton height={120} />
            <Skeleton height={80} />
            <Skeleton height={420} />
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={load}>
                새로고침
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && data && (
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", md: "row" }, 
              gap: { xs: 1.5, sm: 2, md: 3 }
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <HeroBanner
                displayName={data.user.userName}
                profileCompletionPercent={data.user.profileCompletionPercent}
                newCardCount={data.cupidCards.filter(c => shouldRenderCard(c)).length}
                heartBalance={heartBalance}
                unreadMessageCount={unreadMessageCount}
              />
              <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: { xs: 'block', md: 'none' }, px: { xs: 2, sm: 0 } }}>
                <QuickActions
                  onProfile={handleProfile}
                  onHeartsPurchase={handleHeartsPurchase}
                  onMessages={handleMessages}
                  unreadCount={unreadMessageCount}
                />
              </Box>

              <Box id="cupid-cards-section" sx={{ mt: { xs: 2, sm: 2.5, md: 3 }, px: { xs: 2, sm: 0, md: 0 } }}>
                <CupidCardsSection
                  cards={data.cupidCards}
                  walletHearts={heartBalance}
                  onOpen={handleOpen}
                  onExtend={handleExtend}
                  onChat={handleChat}
                />
              </Box>
            </Box>

            <Box sx={{
              width: { md: "280px" },
              flexShrink: 0,
              position: { md: "sticky" },
              top: { md: 72 },
              alignSelf: { md: "flex-start" },
              mt: { xs: 3, md: 0 },
              px: { xs: 2, sm: 0, md: 0 }
            }}>
              <RightRail data={data} />
            </Box>
          </Box>
        )}
      </Container>
        </Box>{/* content */}
      </Box>{/* sidebar+content row */}

      {/* 하트 구매 다이얼로그 */}
      <Dialog open={purchaseDialog} onClose={() => setPurchaseDialog(false)}>
        <DialogTitle>하트 구매</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            하트를 구매해서 더 많은 큐피트 카드를 확인해보세요!
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="10 하트"
                secondary="카드 열기 10회 또는 연장 3회"
              />
              <Typography variant="h6" color="primary">₩9,900</Typography>
            </ListItem>
            <ListItem>
              <ListItemText
                primary="30 하트"
                secondary="카드 열기 30회 또는 연장 10회"
              />
              <Typography variant="h6" color="primary">₩24,900</Typography>
            </ListItem>
            <ListItem>
              <ListItemText
                primary="100 하트"
                secondary="카드 열기 100회 또는 연장 33회"
              />
              <Typography variant="h6" color="primary">₩79,900</Typography>
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseDialog(false)}>취소</Button>
          <Button variant="contained" onClick={() => setPurchaseDialog(false)}>
            구매하기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 프로필 모달 */}
      <MyProfileModal
        open={profileModal}
        onClose={() => setProfileModal(false)}
      />

      {/* 카드 연장 확인 모달 */}
      {extendDialogData && (
        <CardExtendDialog
          open={showExtendDialog}
          cost={extendDialogData.cost}
          isMutual={extendDialogData.isMutual}
          onConfirm={handleExtendConfirm}
          onCancel={handleExtendCancel}
          onCharge={handleHeartsPurchase}
        />
      )}
    </Box>
  );
}